import React from "react";
import { useStoreState } from "../hooks/ep";
import {
  useEnsureActionReportsData,
  useEnsureTripNotesData,
} from "../helpers/useEnsureData";
import AgTable from "./AgTable";
import "styled-components/macro";
import { useMaybeWithSaxecapDevColDefs } from "./saxecap-dev-coldefs";
import { Spinner } from "@blueprintjs/core";

import {
  ACTUAL_COUNT__COLDEF,
  NUM_PASSENGERS__COLDEF,
  OG_BASELINE_SAVINGS_COLDEF,
  OG_BASELINE_TOTAL_SAVINGS_COLDEF,
  OG_CA_FARE_COLDEF,
  OG_CA_SAVINGS_COLDEF,
  OG_CA_TOTAL_SAVINGS_COLDEF,
  OG_FARE_COLDEF,
  OG_TARGET_FARE_COLDEF,
  OG_TARGET_SAVINGS_COLDEF,
  OG_TARGET_TOTAL_SAVINGS_COLDEF,
  ONEWAY__ARRIVES_AT,
  ONEWAY__DEPARTS_AT,
  ONEWAY__DEST,
  ONEWAY__OG_CARRIER_COLDEF,
  ONEWAY__OG_FLIGHTNO_COLDEF,
  ONEWAY__ORIG,
  PLANNER__COLDEF,
  REPORT_ACTION_BTN_COLDEF,
  ROUNDTRIP_F1__ARRIVES_AT,
  ROUNDTRIP_F1__DEPARTS_AT,
  ROUNDTRIP_F1__DEST,
  ROUNDTRIP_F1__OG_CARRIER_COLDEF,
  ROUNDTRIP_F1__OG_FLIGHTNO_COLDEF,
  ROUNDTRIP_F1__ORIG,
  ROUNDTRIP_F2__ARRIVES_AT,
  ROUNDTRIP_F2__DEPARTS_AT,
  ROUNDTRIP_F2__DEST,
  ROUNDTRIP_F2__OG_CARRIER_COLDEF,
  ROUNDTRIP_F2__OG_FLIGHTNO_COLDEF,
  ROUNDTRIP_F2__ORIG,
  XCA__SALES__COLDEF,
  XCA__TCC__COLDEF,
} from "./common-coldefs";
import { integerVF, moneyVF } from "./AgTable/gridOptions";
import _ from "lodash";

function TableInner() {
  const rowData = useStoreState((s) => s.actionReports.skippingsRowData);
  const colDefs = useMaybeWithSaxecapDevColDefs(DEFAULT_COL_DEFS);

  return (
    <AgTable rowData={rowData} columnDefs={colDefs} tableName="skippings" />
  );
}

export function SkippingsTable() {
  const loading = [useEnsureActionReportsData(), useEnsureTripNotesData()];
  return _.max(loading) ? <Spinner /> : <TableInner />;
}

const DEFAULT_COL_DEFS = [
  {
    headerName: "Submitter",
    field: "submitted_by",
    type: "categoryColumn",
    valueFormatter: ({ value }) => value.split("@")[0],
  },
  {
    // TODO: Format as user's timezone
    headerName: "Submitted",
    field: "submitted_at",
    type: "timestampColumn",
    __timezone: "auto",
  },
  //
  // User-submitted booking details
  //
  {
    headerName: "Reasons",
    field: "reasons",
    type: "categoryColumn",
  },
  {
    headerName: "Quoted Price",
    field: "price",
    type: "moneyColumn",
    valueFormatter: (params) => (!params.value ? "" : moneyVF(params)),
  },
  {
    headerName: "Quoted Count",
    field: "numTickets",
    type: "integerColumn",
    valueFormatter: (params) => (!params.value ? "" : integerVF(params)),
  },
  {
    headerName: "Quoted Flight No.",
    field: "flightNumber",
    type: "textColumn",
  },
  {
    headerName: "Quote Source",
    field: "quoteSource",
    type: "textColumn",
  },
  {
    headerName: "Notes",
    field: "notes",
    type: "textColumn",
  },
  {
    headerName: "Resubscribe",
    field: "resubscribe",
    type: "booleanColumn",
  },
  //
  // Comparing to Target Fare
  //
  {
    headerName: "Target Fare",
    field: "Target Fare",
    type: "moneyColumn",
  },
  //
  // Comparing to Current Avg Fare
  //
  {
    headerName: "CA Fare",
    field: "Current Avg Fare",
    type: "moneyColumn",
  },
  ONEWAY__OG_CARRIER_COLDEF,
  ROUNDTRIP_F1__OG_CARRIER_COLDEF,
  ROUNDTRIP_F2__OG_CARRIER_COLDEF,

  ONEWAY__OG_FLIGHTNO_COLDEF,
  ROUNDTRIP_F1__OG_FLIGHTNO_COLDEF,
  ROUNDTRIP_F2__OG_FLIGHTNO_COLDEF,

  OG_FARE_COLDEF,

  ONEWAY__ORIG,
  ROUNDTRIP_F1__ORIG,
  ROUNDTRIP_F2__ORIG,

  ONEWAY__DEST,
  ROUNDTRIP_F1__DEST,
  ROUNDTRIP_F2__DEST,

  ONEWAY__DEPARTS_AT,
  ROUNDTRIP_F1__DEPARTS_AT,
  ROUNDTRIP_F2__DEPARTS_AT,

  ONEWAY__ARRIVES_AT,
  ROUNDTRIP_F1__ARRIVES_AT,
  ROUNDTRIP_F2__ARRIVES_AT,

  NUM_PASSENGERS__COLDEF,
  {
    headerName: "Est Cnt",
    field: "Est Cnt",
    type: "integerColumn",
  },
  ACTUAL_COUNT__COLDEF,
  {
    // TODO: roundtrip
    headerName: "Google",
    field: "Google Flights URL",
    type: "urlColumn",
    __itinMode: "oneway",
  },
  {
    // TODO: roundtrip
    headerName: "Kayak",
    field: "Kayak URL",
    type: "urlColumn",
    __itinMode: "oneway",
  },
  //
  // Comparing to Current Avg Fare
  //
  //
  // Original values
  //
  OG_BASELINE_SAVINGS_COLDEF,
  OG_BASELINE_TOTAL_SAVINGS_COLDEF,
  //
  OG_TARGET_FARE_COLDEF,
  OG_TARGET_SAVINGS_COLDEF,
  OG_TARGET_TOTAL_SAVINGS_COLDEF,
  //
  OG_CA_FARE_COLDEF,
  OG_CA_SAVINGS_COLDEF,
  OG_CA_TOTAL_SAVINGS_COLDEF,
  {
    headerName: "Trip ID",
    field: "[Book] TripId",
    type: "textColumn",
  },
  {
    headerName: "Leg",
    field: "leg",
    type: "categoryColumn",
    __itinMode: "oneway",
  },
  PLANNER__COLDEF,
  XCA__TCC__COLDEF,
  XCA__SALES__COLDEF,
  {
    headerName: "Type",
    field: "Budget Tour Type",
    type: "categoryColumn",
    __appMode: "dvy",
  },
  {
    headerName: "Group#",
    field: "[Book] Group#",
    type: "textColumn",
    __appMode: "dvy",
  },
  {
    headerName: "Tour#",
    field: "[Book] Tour#",
    type: "integerColumn",
    __appMode: "dvy",
  },
  {
    headerName: "School",
    field: "[Book] School",
    type: "textColumn",
    __appMode: "dvy",
  },
  {
    headerName: "A/P",
    field: "A/P",
    type: "categoryColumn",
    __appMode: "dvy",
  },
  REPORT_ACTION_BTN_COLDEF,
];
